import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Grid,
  TextField,
  Typography,
  Box,
  InputAdornment,
  Chip,
  Stack,
} from '@mui/material';
import AiCardBase from './AiCardBase';

const extractVitalData = (data, consultationId) => {
  if (!data) return {};

  let list = null;
  if (Array.isArray(data)) {
    list = data;
  } else if (Array.isArray(data.data)) {
    list = data.data;
  } else if (Array.isArray(data.records)) {
    list = data.records;
  } else if (Array.isArray(data.readings)) {
    list = data.readings;
  }

  if (list) {
    if (list.length === 0) return {};

    // 1. First priority: match consultationId from URL param
    if (consultationId) {
      const match = list.find((item) => item?.consultationId === consultationId);
      if (match) return match;
    }

    // 2. Second priority: item with isCurrent: true
    const currentItem = list.find(
      (item) => item?.isCurrent === true || item?.isCurrent === 'true'
    );
    if (currentItem) return currentItem;

    // 3. Fallback: last item or first item
    return list[list.length - 1] || list[0] || {};
  }

  if (typeof data === 'object') {
    return data;
  }

  return {};
};

const VitalsAiCard = ({ card = {}, onConfirm, isConfirmed, isConfirming, consultationId: propConsultationId }) => {
  const { cardType, label, data = {} } = card;
  const routeParams = useParams() || {};
  const consultationId = propConsultationId || routeParams.consultationId;

  const [values, setValues] = useState(() => ({
    ...extractVitalData(data, consultationId),
  }));

  useEffect(() => {
    setValues({ ...extractVitalData(data, consultationId) });
  }, [data, consultationId]);

  const handleChange = (field, val) => {
    setValues((prev) => ({
      ...prev,
      [field]: val === '' ? '' : isNaN(Number(val)) ? val : Number(val),
    }));
  };

  const handleConfirm = () => {
    onConfirm(cardType, values);
  };

  return (
    <AiCardBase
      cardType={cardType}
      label={label || 'Vital Signs'}
      isConfirmed={isConfirmed}
      isConfirming={isConfirming}
      onConfirm={handleConfirm}
      isUpdatedByAi={card.isUpdatedByAi ?? values?.isUpdatedByAi}
    >
      <Box sx={{ py: 1 }}>
        {/* Week badge for non-BP cards */}
        {cardType !== 'bpChart' && values?.week !== undefined && values?.week !== null && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Chip
              label={`Week ${values.week}`}
              size="small"
              sx={{
                fontSize: 11,
                height: 22,
                bgcolor: '#EFF6FF',
                color: '#1D4ED8',
                fontWeight: 600,
              }}
            />
          </Box>
        )}

        {cardType === 'bpChart' && (
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
                  Blood Pressure Reading:
                </Typography>
               
              </Stack>
              {values?.week !== undefined && values?.week !== null && (
                <Chip
                  label={`Week ${values.week}`}
                  size="small"
                  sx={{
                    fontSize: 11,
                    height: 22,
                    bgcolor: '#EFF6FF',
                    color: '#1D4ED8',
                    fontWeight: 600,
                  }}
                />
              )}
            </Stack>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Systolic"
                  type="number"
                  size="small"
                  disabled={isConfirmed}
                  value={values.systolic ?? ''}
                  onChange={(e) => handleChange('systolic', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">mmHg</InputAdornment>,
                  }}
                  data-testid="input-systolic"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Diastolic"
                  type="number"
                  size="small"
                  disabled={isConfirmed}
                  value={values.diastolic ?? ''}
                  onChange={(e) => handleChange('diastolic', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">mmHg</InputAdornment>,
                  }}
                  data-testid="input-diastolic"
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {cardType === 'pulseChart' && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Pulse Rate"
                type="number"
                size="small"
                disabled={isConfirmed}
                value={values.pulse ?? values.heartRate ?? ''}
                onChange={(e) => handleChange('pulse', e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">bpm</InputAdornment>,
                }}
                data-testid="input-pulse"
              />
            </Grid>
          </Grid>
        )}

        {cardType === 'temperatureChart' && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Temperature"
                type="number"
                size="small"
                disabled={isConfirmed}
                value={values.temperature ?? values.temp ?? ''}
                onChange={(e) => handleChange('temperature', e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {values.unit || values.temperatureUnit || '°F'}
                    </InputAdornment>
                  ),
                }}
                data-testid="input-temperature"
              />
            </Grid>
          </Grid>
        )}

        {cardType === 'spo2Chart' && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Oxygen Saturation (SpO2)"
                type="number"
                size="small"
                disabled={isConfirmed}
                value={values.spo2 ?? ''}
                onChange={(e) => handleChange('spo2', e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                data-testid="input-spo2"
              />
            </Grid>
          </Grid>
        )}

        {cardType === 'weightGainChart' && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="Weight"
                type="number"
                size="small"
                disabled={isConfirmed}
                value={values.weight ?? ''}
                onChange={(e) => handleChange('weight', e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                }}
                data-testid="input-weight"
              />
            </Grid>
          </Grid>
        )}
      </Box>
    </AiCardBase>
  );
};

export default VitalsAiCard;
